import { useCallback, useMemo, useState } from "react";

export interface PathDragChange {
  policy_number: string;
  product_type: number | null;
  path_code: number;
  old_x_value: number;
  new_x_value: number;
  old_y_value: number;
  new_y_value: number;
  x_field: 'stock_market_percent' | 'foreign_exposure_percent' | 'foreign_currency_exposure_percent' | 'liquidity_year';
  y_field: 'yield' | 'stock_market_percent' | 'foreign_exposure_percent' | 'foreign_currency_exposure_percent' | 'liquidity_year' | 'standard_deviation';
  timestamp: number;
}

export interface PolicyExposureChanges {
  stock_market_percent?: {
    old_value: number;
    new_value: number;
    path_code: number;
  };
  foreign_currency_exposure_percent?: {
    old_value: number;
    new_value: number;
    path_code: number;
  };
  foreign_exposure_percent?: {
    old_value: number;
    new_value: number;
    path_code: number;
  };
}

export type NewExposureMap = Record<string, PolicyExposureChanges>;

export const usePathDragChanges = () => {
  const [dragChanges, setDragChanges] = useState<PathDragChange[]>([]);
  const [loadedExposureMap, setLoadedExposureMap] = useState<NewExposureMap | null>(null);

  const addDragChange = useCallback((change: Omit<PathDragChange, 'timestamp'>) => {
    setDragChanges(prev => {
      // מצא שינוי קיים עבור אותו מסלול ופוליסה
      const existingIndex = prev.findIndex(
        dc => dc.path_code === change.path_code &&
              dc.policy_number === change.policy_number &&
              dc.x_field === change.x_field &&
              dc.y_field === change.y_field
      );

      const newChange: PathDragChange = {
        ...change,
        timestamp: Date.now(),
      };

      if (existingIndex >= 0) {
        // עדכן את השינוי הקיים
        const updated = [...prev];
        // שמור על old_x_value ו-old_y_value המקוריים
        updated[existingIndex] = {
          ...newChange,
          old_x_value: prev[existingIndex].old_x_value,
          old_y_value: prev[existingIndex].old_y_value,
        };
        // console.log('Path drag change updated:', updated[existingIndex]);
        return updated;
      } else {
        // הוסף שינוי חדש
        // console.log('Path drag change recorded:', newChange);
        return [...prev, newChange];
      }
    });
  }, []);

  const clearDragChanges = useCallback(() => {
    setDragChanges([]);
    setLoadedExposureMap(null);
  }, []);

  const loadExposureMap = useCallback((map: NewExposureMap) => {
    setDragChanges([]);
    setLoadedExposureMap(map);
  }, []);

  const removeDragChange = useCallback((index: number) => {
    setDragChanges(prev => prev.filter((_, i) => i !== index));
  }, []);

  const removePolicyChanges = useCallback((policyNumber: string) => {
    setDragChanges(prev => prev.filter(change => change.policy_number !== policyNumber));
  }, []);

  // Build semantic exposure map from drag changes, merged with any loaded map
  const newExposureMap: NewExposureMap = useMemo(() => {
    const map: NewExposureMap = { ...(loadedExposureMap ?? {}) };

    dragChanges.forEach(change => {
      const { policy_number, y_field, old_y_value, new_y_value, path_code } = change;

      // Only process exposure-related fields
      if (
        y_field === 'stock_market_percent' ||
        y_field === 'foreign_currency_exposure_percent' ||
        y_field === 'foreign_exposure_percent'
      ) {
        if (!map[policy_number]) {
          map[policy_number] = {};
        }

        map[policy_number][y_field] = {
          old_value: old_y_value,
          new_value: new_y_value,
          path_code,
        };
      }
    });

    return map;
  }, [dragChanges, loadedExposureMap]);

  return {
    dragChanges, // Legacy - for backward compatibility
    newExposureMap, // New semantic map
    addDragChange,
    clearDragChanges,
    removeDragChange,
    removePolicyChanges,
    loadExposureMap,
  };
};
